import debounce from 'lodash.debounce';
import { useState, useEffect, useRef, useCallback } from 'react';
import APIEndpoints from 'src/Member/api/APIEndpoints';
import ToastMessage from 'src/Member/components/common/ToastMessage';
import { toast } from 'react-toastify';
import { arrayBufferToBase64 } from 'src/Member/utils/common';
import { axiosInstance } from 'src/api/axios';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import useMessageAndNotification from '../../common-hook/useMessageAndNotification';

const useMyMessage = ({
  messageSelectedFromPopup,
  setIsComposed,
  selectedTabLocal,
}) => {
  const SortByDummyOptions = [
    { label: 'Latest', value: 'desc' },
    { label: 'Oldest', value: 'asc' },
  ];

  const AttachmentOptions = [
    //{ label: 'Select', value: '' },
    { label: 'With Attachment', value: true },
    { label: 'Without Attachment', value: false },
  ];

  const [popUpMessageData, setPopUpMessageData] = useState(
    messageSelectedFromPopup,
  );
  const [messageDetailData, setMessageDetailData] = useState(null);

  const [initialRender, setInitialRender] = useState(true);

  //for sortby
  const [sortByData, setSortByData] = useState(SortByDummyOptions[0]);
  //for type
  const [typeData, setTypeData] = useState([]);
  //for attachments
  const [attachmentData, setAttachmentData] = useState([]);
  //for search
  const [searchText, setSearchText] = useState('');
  const [searchTextDebounce, setSearchTextDebounce] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [replyEnabled, setReplyEnabled] = useState(false);

  const [listLoading, setListLoading] = useState(false);
  const [listApiDataNotFoundError, setListApiDataNotFoundError] =
    useState(false);
  const [listApiServerError, setListApiServerError] = useState(false);

  const [messageDetailLoading, setMessageDetailLoading] = useState(false);
  const [
    messageDetailApiDataNotFoundError,
    setMessageDetailApiDataNotFoundError,
  ] = useState(false);
  const [messageDetailApiServerError, setMessageDetailApiServerError] =
    useState(false);

  const [selectedTab, setSelectedTab] = useState('inbox');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedIndexForSent, setSelectedIndexForSent] = useState(0);

  //pagination specific states
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const displayPaginationMininumCount = 10;
  const recordsPerPage = useRef(null);

  const pageSizeHandler = (pageSizeInfo) => {
    setPageSize(pageSizeInfo.value);
    setCurrentPage(1);
    setSelectedIndexForSent(0);
    setSelectedIndex(0);
    setTimeout(() => {
      if (recordsPerPage && recordsPerPage.current) {
        recordsPerPage.current.focus();
      }
    }, 3000);
  };

  const paginationHandler = (page) => {
    setCurrentPage(page);
    setSelectedIndexForSent(0);
    setSelectedIndex(0);
  };

  //Search filter functionality
  const onSearch = (event) => {
    setSearchText(event.target.value);
  };

  //Reset Filter function
  const resetFilterHandler = () => {
    setCurrentPage(1);
    setPageSize(10);
    setSortByData(SortByDummyOptions[0]);
    setTypeData([]);
    setAttachmentData([]);
    setSearchText('');
    setListApiDataNotFoundError(false);
    setListApiServerError(false);
    setMessageDetailData(null);
    setPopUpMessageData(null);
  };

  const [files, setFiles] = useState([]);
  const [filesLoader, setfilesLoader] = useState(false);
  const [subject, setSubject] = useState('');
  const [sendDocumentFor, setSendDocumentFor] = useState();
  const [refreance, setRefreance] = useState('');
  const [typeOptionList, setTypeOptionList] = useState([]);
  const [docType, setDocType] = useState();
  const [comments, setComments] = useState('');
  const [typeListValues, setTypeListValues] = useState([]);

  const formRef = useRef(null);

  const { updateNotificationCount } = useMessageAndNotification({
    filteredData,
    setFilteredData,
    messageDetailData,
    setMessageDetailData,
  });

  //#TODO : need to call updated API to get Type dropdown option for Compose Message Screen
  const getTypeList = async () => {
    try {
      const responseTypeCategory = await axiosInstance.get(
        APIEndpoints.getTypeCategoryList(),
      );
      const typeListData =
        responseTypeCategory?.data?.Response?.MessageCategoryMaster;

      const typeCategoryValues = typeListData.map((item) => {
        return {
          label: item?.Categorydesc,
          value: item?.Categorycode,
          ...item,
        };
      });
      const uniqueTypeData = [];
      for (let i = 0; i < typeCategoryValues.length; i++) {
        if (
          !uniqueTypeData.find(
            (item) => item.label === typeCategoryValues[i]?.value,
          )
        ) {
          uniqueTypeData.push(typeCategoryValues[i]);
        }
      }
      setTypeListValues(typeListData);
      setTypeOptionList(uniqueTypeData);
    } catch (err) {
      console.log('err :', err);
    }
  };

  const sendNewMessageFormRef = useRef(null);
  const sendReplyMessageFormRef = useRef(null);

  useEffect(() => {
    if (isComposing) {
      setTimeout(() => {
        if (sendNewMessageFormRef?.current) {
          sendNewMessageFormRef?.current?.focus();
        }
      }, 500);
    }
  }, [isComposing]);

  useEffect(() => {
    if (
      (searchText?.length > 2 || searchText?.length === 0) &&
      !initialRender
    ) {
      debouncedCall(searchText);
    }
  }, [searchText]);

  useEffect(() => {
    if (
      (searchTextDebounce?.length > 2 || searchTextDebounce?.length === 0) &&
      !initialRender
    ) {
      getSendReceiveMessageList();
    }
  }, [searchTextDebounce]);

  const debouncedCall = useCallback(
    debounce((currentVal) => setSearchTextDebounce(currentVal), 500),
    [],
  );

  useEffect(() => {
    setCurrentPage(1);
    if (!initialRender) {
      getSendReceiveMessageList();
      setPopUpMessageData(null);
    }
    setMessageDetailData(null);
  }, [sortByData, typeData, attachmentData, pageSize]);

  useEffect(() => {
    resetFilterHandler();
  }, [selectedTab]);

  useEffect(() => {
    if (!initialRender) {
      getSendReceiveMessageList();
      setPopUpMessageData(null);
    }
    setMessageDetailData(null);
  }, [currentPage]);

  const getSendReceiveMessageList = async (eventType = 'messages') => {
    setListLoading(true);
    let eventTypeOid = '74c790f3-6cf8-423b-a286-2bfc2430272a';
    if (eventType === 'messages') {
      eventTypeOid = '74c790f3-6cf8-423b-a286-2bfc2430272a';
    } else if (eventType === 'document') {
      eventTypeOid = '';
    } else if (eventType === 'notification') {
      eventTypeOid = '';
    }
    let filterParams = '';
    if (sortByData?.value) {
      filterParams = filterParams + `SortDirection/${sortByData?.value}`;
    }
    if (selectedTab) {
      filterParams =
        filterParams +
        `/messagesentreceived/${selectedTab === 'inbox' ? 'R' : 'S'}`;
    }
    if (searchText?.length > 2) {
      filterParams = filterParams + `/searchkey/${searchText}`;
    }
    if (typeData?.value) {
      filterParams = filterParams + `/type/${typeData?.value}`;
    }
    if (attachmentData?.label) {
      filterParams = filterParams + `/attachments/${attachmentData?.value}`;
    }
    if (currentPage) {
      filterParams = filterParams + `/pagenumber/${currentPage}`;
    }
    if (pageSize) {
      filterParams = filterParams + `/pagesize/${pageSize}`;
    }

    try {
      setListApiDataNotFoundError(false);
      setListApiServerError(false);
      const responseMessageListResp = await axiosInstance.get(
        `${APIEndpoints.getSendReceiveMessage()}${eventTypeOid}${
          filterParams && `/${filterParams}`
        }`,
      );
      const { Pagination } = responseMessageListResp?.data?.Response || {
        PageNumber: 1,
        PageSize: 10,
        TotalRecords: 0,
      };
      const responseMessageList =
        responseMessageListResp.data.Response.listOfRRMessageDTOObj;
      const listData = [];
      if (responseMessageList?.length > 0) {
        responseMessageList.map((item, index) => {
          listData.push({
            id: item?.oid || index,
            indexValue: index,
            FirstName: '',
            LastName: '',
            EventTitle: item?.eventtitle,
            EventDesc: item?.eventdesc,
            MessagecategorymasterDTOObj: {
              Categorydesc: item?.messagecategorymaster?.categorydesc,
            },
            isDmsDoc: item?.DmsDocument,
            attachments: item?.attachments,
            attachmentsDetail: item?.rrmessageDocumentDetailDTO,
            ReceivedDateTime: item?.receiveddatetime,
            MessageSentReceived: item?.messagesentreceived,
            ...item,
          });
          return 0;
        });
        console.log('listData', listData);
        setFilteredData(listData);
        if (
          listData.findIndex(
            (item) => item?.id === messageSelectedFromPopup?.Oid,
          ) > -1 &&
          messageSelectedFromPopup?.Oid
        ) {
          setSelectedIndex(
            listData.findIndex(
              (item) => item?.id === messageSelectedFromPopup?.Oid,
            ),
          );
          const filterData = listData.filter(
            (item) => item?.id === messageSelectedFromPopup?.Oid,
          );
          getSendReceiveMessageDetail(
            filterData[0]?.callid || filterData[0]?.EventTitle,
            Boolean(filterData[0]?.callid),
            filterData[0]?.oid,
            filterData[0]?.isread,
          );
          //setMessageDetailData(
          //listData.filter(
          //(item) => item.id === messageSelectedFromPopup?.Oid,
          //)[0],
          //);
        } else {
          setSelectedIndex(0);
          console.log('log default item@', listData[0]);
          getSendReceiveMessageDetail(
            listData[0]?.callid || listData[0]?.EventTitle,
            Boolean(listData[0]?.callid),
            listData[0]?.oid,
            listData[0]?.isread,
          );
        }
        setTotalRecords(Pagination?.TotalRecords);
      }
    } catch (error) {
      console.log('error :', error);
      if (error?.toString()?.includes('404')) {
        setListApiDataNotFoundError(true);
        setListApiServerError(true);
      }
      setListApiServerError(true);
      setFilteredData([]);
      setTotalRecords(0);
    } finally {
      setListLoading(false);
    }
  };

  const markeMessageAsRead = (oid, isRead = true) => {
    const payload = {
      ReadFlag: [
        {
          oid: oid,
          IsRead: isRead,
        },
      ],
    };
    //eslint-disable-next-line no-unused-vars
    const responseMessageListResp = axiosInstance.post(
      APIEndpoints.markMessageIsRead(),
      payload,
    );
    responseMessageListResp
      .then((res) => {
        if (res?.status === 201) {
          updateNotificationCount();
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getSendReceiveMessageDetail = async (
    searchTextData,
    isCallIdPresent = false,
    selectedMessageOid,
    isRead,
  ) => {
    setMessageDetailLoading(true);
    const eventTypeOid = '74c790f3-6cf8-423b-a286-2bfc2430272a';
    let filterParams = '';
    if (sortByData?.value) {
      filterParams = filterParams + `SortDirection/${sortByData?.value}`;
    }
    if (searchTextData) {
      filterParams =
        filterParams +
        `/${isCallIdPresent ? 'searchkey' : 'title'}/${
          isCallIdPresent ? searchTextData : btoa(searchTextData)
        }`;
    }
    if (pageSize) {
      filterParams = filterParams + `/pagesize/${pageSize}`;
    }

    try {
      setMessageDetailApiDataNotFoundError(false);
      setMessageDetailApiServerError(false);
      const responseMessageListResp = await axiosInstance.get(
        `${APIEndpoints.getSendReceiveMessage()}${eventTypeOid}${
          filterParams && `/${filterParams}`
        }`,
      );
      const responseMessageList =
        responseMessageListResp.data.Response.listOfRRMessageDTOObj;
      responseMessageList.sort(
        (a, b) => new Date(b?.receiveddatetime) - new Date(a?.receiveddatetime),
      );
      const listData = [];
      if (responseMessageList?.length > 0) {
        responseMessageList.map((item, index) => {
          listData.push({
            id: item?.oid || index,
            indexValue: index,
            FirstName: '',
            LastName: '',
            EventTitle: item?.eventtitle,
            EventDesc: item?.eventdesc,
            MessagecategorymasterDTOObj: {
              Categorydesc: item?.messagecategorymaster?.categorydesc,
            },
            isDmsDoc: item?.DmsDocument,
            attachments: item?.attachments,
            attachmentsDetail: item?.rrmessageDocumentDetailDTO,
            ReceivedDateTime: item?.receiveddatetime,
            ...item,
          });
          return 0;
        });
      }
      setReplyEnabled(false);
      setMessageDetailData(listData);
      !isRead &&
        selectedTabLocal === 'messages' &&
        markeMessageAsRead(selectedMessageOid, true);
    } catch (error) {
      console.log('error :', error);
      if (error?.toString()?.includes('404')) {
        setMessageDetailApiDataNotFoundError(true);
        setMessageDetailApiServerError(true);
      }
      setMessageDetailApiServerError(true);
      setTotalRecords(0);
    } finally {
      setMessageDetailLoading(false);
    }
  };

  const schema = yup.object({
    subject: yup.string().required('Enter Subject '),
    type: yup.object().required('Please select a type'),
    refreance: yup.string().when('file', (file, schema) => {
      if (file[0] && file[0][0] && docType?.value === 'Claims') {
        return schema.required('Enter Refrance');
      } else {
        return schema.notRequired();
      }
    }),
    file: yup[files?.length > 0 ? 'object' : 'array']().nullable(),
    //comments: yup.string().required('Enter message '),
  });

  const replyMessageSchema = yup.object({
    comments: yup.string().required('Enter message '),
  });

  const userData = useSelector((state) => state?.user?.userData);

  const commonPayload = {
    UserType: userData?.UserType,
    Caller: userData?.MemberDetails?.MemberName,
    Customer: userData?.MemberDetails?.MemberName,
    Callertype: userData?.UserType === 'Employee' ? '4' : '3',
    CustomerType: userData?.UserType,
    UserOid: userData?.Oid, //userData?.UserOid
    CustomerOid: userData?.MemberDetails?.MemberOID,
    MessageSentTo: 'Javelina',
    MessageTriggerFrom: 'P',
  };

  const composeApiCaller = async (values) => {
    setIsComposed && setIsComposed(false);

    function toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        console.log('reader', reader);
        reader.onload = () => {
          resolve({
            LinkType: 'Image',
            OrigFileName: file?.name,
            StringContAFile: reader?.result?.split(',')[1],
          });
          console.log('file onload', file);
        };
        reader.onerror = (error) => reject(error);
      });
    }

    const filePathsPromises = [];
    if (Object?.values(values?.file)?.length > 0) {
      const data = Object?.values(values?.file);
      data.forEach((item) => {
        filePathsPromises.push(toBase64(item.file));
      });
    }
    const filePaths = await Promise.all(filePathsPromises);
    const docs = filePaths.map((res) => res);

    const payload = {
      ...commonPayload,
      EventTypeOid: '74c790f3-6cf8-423b-a286-2bfc2430272a',
      EventTitle: values?.subject,
      EventDesc: values?.comments,
      DocumentEntityType: values?.type?.MappedEntity,
      MessageCategoryoid: values?.type?.Oid,
      Reference: values?.refreance ? values?.refreance : '',
      ReplyMessage: false,
      CRMCallid: '',
      DocumentDetails: docs,
    };
    if (values?.refreance) {
      payload['LinkRecords'] = {
        LinkType: 'Claim',
        ClaimNumber: values?.refreance,
      };
    }
    try {
      const response = await axiosInstance.post(
        APIEndpoints.composeMessageResource(),
        payload,
      );
      console.log('response', response);
      if (response?.data) {
        setIsComposed && setIsComposed(true);
        toast.success(
          <ToastMessage
            type="success"
            //title="Your message has been successfully delivered!"
            subTitle="Your message has been successfully delivered!"
          />,
        );
        setIsComposing(false);
        setReplyEnabled(false);
      }
    } catch (err) {
      console.log('err', err);
      toast.error(
        <ToastMessage
          type="error"
          title="Sorry! Your message could not be delivered!"
          subTitle="Check if you have entered all the details correctly and try again."
        />,
      );
    }
  };

  const submitComposeMessage = (values) => {
    console.log('values :', values);
    let msg = '';

    if (values?.file?.length > 0 && values?.type?.MappedEntity === 'CLAIM') {
      if (!values?.refreance) {
        msg = 'Please enter reference number';
      }
    }

    if (msg) {
      toast.error(
        <ToastMessage
          type="error"
          title="Sorry! Your message could not be delivered!"
          subTitle="Check if you have entered all the details correctly and try again."
        />,
      );
    } else {
      composeApiCaller(values);
    }
  };

  const replyApiCaller = async (values) => {
    console.log('reply :', values);
    function toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        console.log('reader', reader);
        reader.onload = () => {
          resolve({
            LinkType: 'Image',
            OrigFileName: file?.name,
            StringContAFile: reader?.result?.split(',')[1],
          });
          console.log('file onload', file);
        };
        reader.onerror = (error) => reject(error);
      });
    }

    const filePathsPromises = [];
    if (Object?.values(values?.file)?.length > 0) {
      const data = Object?.values(values?.file);
      data.forEach((item) => {
        filePathsPromises.push(toBase64(item.file));
      });
    }
    const filePaths = await Promise.all(filePathsPromises);
    const docs = filePaths.map((res) => res);

    const payload = {
      ...commonPayload,
      EventTypeOid: values?.eventtypeoid,
      EventTitle: values?.EventTitle,
      EventDesc: values?.comments,
      DocumentEntityType: values?.type?.MappedEntity,
      MessageCategoryoid: values?.messagecategoryoid, //messagecategoryoid
      Reference: values?.Reference ? values?.Reference : '',
      ReplyMessage: values?.callid ? true : false,
      CRMCallid: values?.callid,
      DocumentDetails: docs,
    };
    if (values?.Reference) {
      payload['LinkRecords'] = {
        LinkType: 'Claim',
        ClaimNumber: values?.Reference,
      };
    }
    try {
      const response = await axiosInstance.post(
        APIEndpoints.composeMessageResource(),
        payload,
      );
      console.log('response', response);
      setIsComposed && setIsComposed(true);
      if (response?.data) {
        toast.success(
          <ToastMessage
            type="success"
            //title="Your message has been successfully delivered!"
            subTitle="Your message has been successfully delivered!"
          />,
        );
        setIsComposing(false);
        setReplyEnabled(false);
      }
    } catch (err) {
      console.log('err', err);
      toast.error(
        <ToastMessage
          type="error"
          title="Sorry! Your message could not be delivered!"
          subTitle="Check if you have entered all the details correctly and try again."
        />,
      );
    }
  };

  const submitReplyMessage = (values, selectedData) => {
    replyApiCaller({ ...values, ...selectedData });
  };
  const getSanitizedContent = (content) => {
    let modifiedContent = '';
    if (
      content?.toString()?.startsWith('"') &&
      content?.toString()?.endsWith('"')
    ) {
      modifiedContent = content?.substring(1, content?.length - 1);
    } else {
      modifiedContent = content;
    }
    const splitedContent = modifiedContent
      ?.toString()
      ?.replace(/\\n/g, '')
      ?.replace(/=\\/g, '=')
      ?.replace(/\n/g, '<br />');
    return splitedContent;
  };

  const downloadFile = async (reportName, byte, contentType) => {
    const actualData = arrayBufferToBase64(byte);
    const link = document.createElement('a');
    link.setAttribute('href', `data:${contentType};base64,` + actualData);
    link.setAttribute('download', reportName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadDmsDocumentHandler = async (item) => {
    try {
      const documentData = await axiosInstance.get(
        `${APIEndpoints.getDocument() + item?.Oid}`,
        { responseType: 'arraybuffer' },
      );
      const contentType = documentData?.headers['content-type'];
      downloadFile(item?.OrigFileName, documentData?.data, contentType);
    } catch (error) {
      console.log('error :', error);
      if (error?.toString()?.includes('404')) {
        console.log('404 error');
        toast.error(
          <ToastMessage
            type="error"
            title="File not found!"
            subTitle="Could not download the file. Contact your Administrator for more information."
          />,
        );
      } else {
        toast.error(
          <ToastMessage
            type="error"
            title="Something went wrong!"
            subTitle="Could not download the file. Try again later."
          />,
        );
      }
    }
  };

  const downloadNonDmsDocumentHandler = async (item) => {
    try {
      const docLocation = btoa(item?.Location);
      const responseDetails = await axiosInstance.get(
        `${APIEndpoints.getDocumentsDownload()}doclocation/${docLocation}/id/${
          item?.DocControlNo
        }`,
        { responseType: 'arraybuffer' },
      );
      const contentType = responseDetails.headers['content-type'];
      downloadFile(item?.OrigFileName, responseDetails?.data, contentType);
    } catch (error) {
      if (error?.toString()?.includes('404')) {
        console.log('404 error');
        toast.error(
          <ToastMessage
            type="error"
            title="File not found!"
            subTitle="Could not download the file. Contact your Administrator for more information."
          />,
        );
      } else {
        toast.error(
          <ToastMessage
            type="error"
            title="Something went wrong!"
            subTitle="Could not download the file. Try again later."
          />,
        );
      }
    }
  };

  const downloadFileHandler = async (item) => {
    if (item?.isDmsDoc) {
      downloadDmsDocumentHandler(item);
    } else {
      downloadNonDmsDocumentHandler(item);
    }
  };
  const downloadAllFileHandler = async (items) => {
    items?.attachmentsDetail?.map(async (file) => {
      const uploadResponse = await (items?.isDmsDoc
        ? downloadDmsDocumentHandler(file)
        : downloadNonDmsDocumentHandler(file));
      return uploadResponse;
    });
  };

  useEffect(() => {
    getTypeList();
    setInitialRender(false);
  }, []);
  return {
    sortByData,
    searchText,
    typeData,
    SortByDummyOptions,
    setSortByData,
    setTypeData,
    AttachmentOptions,
    attachmentData,
    setAttachmentData,
    onSearch,
    resetFilterHandler,
    filteredData,
    pageSize,
    currentPage,
    totalRecords,
    displayPaginationMininumCount,
    pageSizeHandler,
    paginationHandler,
    setCurrentPage,
    setTotalRecords,
    setPageSize,
    isComposing,
    setIsComposing,
    sendNewMessageFormRef,
    sendReplyMessageFormRef,
    replyEnabled,
    setReplyEnabled,
    schema,
    replyMessageSchema,
    submitComposeMessage,
    submitReplyMessage,
    files,
    setFiles,
    filesLoader,
    subject,
    sendDocumentFor,
    refreance,
    docType,
    comments,
    typeOptionList,
    formRef,
    setComments,
    setDocType,
    setfilesLoader,
    setSubject,
    setSendDocumentFor,
    setRefreance,
    typeListValues,
    getSendReceiveMessageList,
    listLoading,
    listApiDataNotFoundError,
    listApiServerError,
    selectedTab,
    setSelectedTab,
    setSelectedIndex,
    selectedIndex,
    setSelectedIndexForSent,
    selectedIndexForSent,
    setMessageDetailData,
    messageDetailData,
    getSendReceiveMessageDetail,
    typeOptionList,
    getSanitizedContent,
    downloadFileHandler,
    downloadAllFileHandler,
    messageDetailLoading,
    messageDetailApiServerError,
    messageDetailApiDataNotFoundError,
    recordsPerPage,
  };
};

export default useMyMessage;
